import { translatePath, matchEnPath, matchSlPath, type Lang, SUPPORTED_LANGS } from "./routeMap";
import { translateSlug } from "@/lib/newsSlugMap";
import { spotWeldingSlug, resolveSpotWeldingId } from "@/lib/spotWeldingSlugMap";

/**
 * Convert an EN-style absolute path (the form used everywhere in source code) into
 * a fully lang-prefixed URL for the active language.
 *
 *   localizePath("/products/welding-heads", "sl")
 *     → "/sl/izdelki/varilne-glave"
 *
 * Hash fragments (`#foo`) and search strings (`?bar=1`) are preserved.
 */
export function localizePath(enPath: string, lang: Lang): string {
  if (!enPath) return enPath;
  if (enPath.startsWith("#") || enPath.startsWith("?")) return enPath;
  if (!enPath.startsWith("/")) return enPath; // external / mailto / tel:

  // Split off hash + search
  const hashIdx = enPath.indexOf("#");
  const searchIdx = enPath.indexOf("?");
  const cutAt =
    hashIdx === -1 ? searchIdx
    : searchIdx === -1 ? hashIdx
    : Math.min(hashIdx, searchIdx);
  const pathOnly = cutAt === -1 ? enPath : enPath.slice(0, cutAt);
  const suffix = cutAt === -1 ? "" : enPath.slice(cutAt);

  // Already lang-prefixed (e.g. someone wrote /sl/... or /en/...)
  const firstSeg = pathOnly.split("/")[1];
  if ((SUPPORTED_LANGS as readonly string[]).includes(firstSeg)) {
    return enPath;
  }

  const translated = pathOnly === "/" ? "" : translatePath(pathOnly, lang);
  // For root, translated === "/" — drop the trailing slash so we get e.g. "/sl"
  if (pathOnly === "/") return `/${lang}${suffix}`;
  // Post-translate spot-welding machine slug (routeMap can't translate the
  // dynamic `:machineId` segment by itself).
  let final = translated;
  const spotMatch = final.match(/^\/(?:products\/spot-welding|izdelki\/tockovno-varjenje)\/([^/?#]+)(.*)$/);
  if (spotMatch) {
    const [, slug, rest] = spotMatch;
    const canonical = resolveSpotWeldingId(slug);
    const targetSlug = spotWeldingSlug(canonical, lang);
    const prefix = lang === "sl" ? "/izdelki/tockovno-varjenje" : "/products/spot-welding";
    final = `${prefix}/${targetSlug}${rest}`;
  }
  return `/${lang}${final}${suffix}`;
}

/**
 * Given any lang-prefixed pathname (e.g. `/sl/izdelki/varilne-glave`), return
 * the canonical EN-style path (`/products/welding-heads`). Returns null if the
 * path doesn't map to a known route.
 */
export function delocalizePathname(pathname: string): { lang: Lang; enPath: string } | null {
  const segs = pathname.split("/");
  const lang = segs[1];
  if (!(SUPPORTED_LANGS as readonly string[]).includes(lang)) return null;
  const rest = "/" + segs.slice(2).join("/");
  const normalized = rest === "/" || rest === "" ? "/" : rest.replace(/\/$/, "");

  if (lang === "en") {
    const m = matchEnPath(normalized);
    if (!m) return { lang: "en", enPath: normalized };
    return { lang: "en", enPath: normalized };
  }
  // lang === "sl"
  const m = matchSlPath(normalized);
  if (!m) return { lang: "sl", enPath: normalized };
  // reconstruct EN path with params
  const enFilled = m.entry.en.replace(/:([a-zA-Z]+)/g, (_, name) => m.params[name] ?? `:${name}`);
  return { lang: "sl", enPath: enFilled };
}

/**
 * Swap a fully lang-prefixed pathname to the other language, preserving
 * params (e.g. `:slug`, `:machineId`).
 */
export function swapLang(pathname: string, target: Lang): string {
  const d = delocalizePathname(pathname);
  if (!d) {
    // unknown path — just swap the lang segment
    const parts = pathname.split("/");
    if ((SUPPORTED_LANGS as readonly string[]).includes(parts[1])) {
      parts[1] = target;
      return parts.join("/") || "/";
    }
    return `/${target}${pathname}`;
  }
  // Translate news article slugs (EN ↔ SL) using registered map.
  // d.enPath is in EN form (e.g. "/news/mfdc-resistance-welding"); for the
  // SL target we need to also translate the slug to its SL form.
  let enPath = d.enPath;
  const newsMatch = enPath.match(/^\/news\/([^/?#]+)(.*)$/);
  if (newsMatch) {
    const [, slug, rest] = newsMatch;
    // enPath always carries the EN slug after delocalize, so:
    const translated = translateSlug(slug, "en"); // ensure EN canonical
    enPath = `/news/${translated}${rest}`;
    // localizePath below will swap the /news segment to /novice for SL; we
    // also need to swap the slug. Do it post-hoc.
    const localized = localizePath(enPath, target);
    if (target === "sl") {
      return localized.replace(
        /\/novice\/([^/?#]+)/,
        (_m, s) => `/novice/${translateSlug(s, "sl")}`,
      );
    }
    return localized;
  }

  // Translate spot-welding machine slugs (EN ↔ SL).
  const spotMatch = enPath.match(/^\/products\/spot-welding\/([^/?#]+)(.*)$/);
  if (spotMatch) {
    const [, slug, rest] = spotMatch;
    const canonicalId = resolveSpotWeldingId(slug);
    const localizedSlug = spotWeldingSlug(canonicalId, target);
    enPath = `/products/spot-welding/${canonicalId}${rest}`;
    const localized = localizePath(enPath, target);
    // Replace the trailing slug segment with the target-language version.
    return localized.replace(
      /(\/(?:spot-welding|tockovno-varjenje)\/)([^/?#]+)/,
      (_m, prefix) => `${prefix}${localizedSlug}`,
    );
  }

  return localizePath(enPath, target);
}
